/**
 * `/invite/accept?token=…` — public accept-invitation surface (ADR-0079).
 *
 * ROOT-LEVEL route (beside `auth.login.tsx`, OUTSIDE `$tenantSlug`) so it
 * mounts without the AuthGuard that lives in `$tenantSlug.tsx` — the invitee
 * has no session yet. This is the public counterpart of the Team page's
 * invite flow.
 *
 * Flow:
 *   1. On mount, GET /api/shell/auth/invitations/{token}.
 *      - Valid   -> {email, tenant_name}: show "You've been invited to
 *        {tenant_name} as {email}" + a password field (min 8) with confirm +
 *        "Set password & join".
 *      - Invalid / expired / used -> ONE generic message (no enumeration,
 *        no email leak — ADR-0079 §3).
 *   2. POST /api/shell/auth/accept-invite {token, password}.
 *      - 204 -> redirect to /auth/login.
 *      - any error -> generic error message.
 *
 * Accessibility (design-intent §G):
 *   - Real <label htmlFor> for both password fields.
 *   - Password min-8 + confirm-match validation announced via role=alert
 *     (focus moves to the alert on failure only).
 *   - The whole card is keyboard-operable; 40px controls; 12px label floor.
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@ken/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";

interface InviteSearch {
  token: string | undefined;
}

export const Route = createFileRoute("/invite/accept")({
  component: AcceptInviteRoute,
  validateSearch: (search: Record<string, unknown>): InviteSearch => ({
    token: typeof search["token"] === "string" ? search["token"] : undefined,
  }),
});

interface InvitationInfo {
  email: string;
  tenant_name: string;
}

const GENERIC_INVALID = "This invitation is invalid or has expired.";

function AcceptInviteRoute(): ReactElement {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const token = search.token;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const errorRef = useRef<HTMLDivElement | null>(null);

  // Validate the token on mount.
  useEffect(() => {
    if (!token) {
      setLoadError(GENERIC_INVALID);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/shell/auth/invitations/${encodeURIComponent(token)}`,
          { credentials: "include" },
        );
        if (cancelled) return;
        if (!res.ok) {
          // Generic — never distinguish not-found / expired / used (§3).
          setLoadError(GENERIC_INVALID);
          return;
        }
        setInfo((await res.json()) as InvitationInfo);
      } catch {
        if (!cancelled) setLoadError(GENERIC_INVALID);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Move focus to the form error on failure only (§G).
  useEffect(() => {
    if (formError && errorRef.current) errorRef.current.focus();
  }, [formError]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);
    if (password.length < 8) {
      setFormError("Your password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("The passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/shell/auth/accept-invite", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (res.status === 204 || res.ok) {
        // Show a brief confirmation, then send them to sign in.
        setSucceeded(true);
        setTimeout(() => {
          void navigate({ to: "/auth/login" });
        }, 1200);
        return;
      }
      // Generic error — no enumeration oracle.
      setFormError(GENERIC_INVALID);
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="grid place-items-center min-h-screen p-4 bg-[var(--color-bg-base)]"
      data-testid="invite-accept-page"
    >
      <Card surface="solid" className="w-full max-w-sm" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="grid place-items-center w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-white font-bold font-[var(--font-hand)] text-[22px]">
              K
            </div>
            <div className="font-[var(--font-hand)] text-[24px] font-semibold">ken</div>
          </div>
          <CardTitle>Accept your invitation</CardTitle>
        </CardHeader>
        <CardContent>
          {succeeded ? (
            <div
              role="status"
              data-testid="invite-accept-success"
              className="text-sm text-[var(--color-status-active-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-active-bg)] border border-[var(--color-status-active-text)]"
            >
              You&apos;re all set. Taking you to sign in…
            </div>
          ) : loading ? (
            <p
              className="text-sm text-[var(--color-text-soft)]"
              data-testid="invite-accept-loading"
            >
              Checking your invitation…
            </p>
          ) : loadError !== null || info === null ? (
            <div
              role="alert"
              data-testid="invite-accept-error"
              className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)]"
            >
              {loadError ?? GENERIC_INVALID}
            </div>
          ) : (
            <>
              <p
                className="mb-4 text-sm text-[var(--color-text-strong)]"
                data-testid="invite-accept-email"
              >
                You&apos;ve been invited to{" "}
                <span className="font-semibold">{info.tenant_name}</span> as{" "}
                <span className="font-semibold">{info.email}</span>. Set a
                password to join.
              </p>
              <form
                data-testid="invite-accept-form"
                className="flex flex-col gap-4"
                onSubmit={(e) => void onSubmit(e)}
                noValidate
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-password">Password</Label>
                  <Input
                    id="invite-password"
                    type="password"
                    placeholder="At least 8 characters"
                    data-testid="invite-accept-password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-confirm">Confirm password</Label>
                  <Input
                    id="invite-confirm"
                    type="password"
                    placeholder="Re-enter your password"
                    data-testid="invite-accept-confirm"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                {formError !== null && (
                  <div
                    ref={errorRef}
                    tabIndex={-1}
                    role="alert"
                    data-testid="invite-accept-form-error"
                    className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
                  >
                    {formError}
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  data-testid="invite-accept-submit"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? "Joining…" : "Set password & join"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
